import { IconButton, Link, Stack, SxProps, Theme, Typography } from '@mui/material';
import { produce } from 'immer';
import { ReactElement, useMemo } from 'react';
import { Tooltip } from 'src/components';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FormField, getFormField } from 'src/components/Forms';
import { SAVE_DEBOUNCE_DELAY } from 'src/constants/delays';
import { autocompleteAISuggestionSx } from './site-documentation.styles';
import { DocumentationFormField } from './site-documentation.types';
import { emptyOption } from './use-site-documentation-form';

interface SubSystemFieldProps {
  field: DocumentationFormField;
  disabled?: boolean;
  autocompleteProps?: Record<string, unknown>;
  textFieldProps?: Record<string, unknown>;
  inputProps?: Record<string, unknown>;
  autocompleteMinWidth?: number | string;
  onFieldChange: (fieldName: string, value: string | null | boolean) => void;
  onResetField: (fieldName: string) => void;
  hasAISuggestion?: boolean;
  link?: string;
}

export const SubSystemField = ({
  field,
  disabled,
  autocompleteProps: externalAutocompleteProps,
  autocompleteMinWidth = '8.5rem',
  textFieldProps,
  inputProps,
  onFieldChange,
  onResetField,
  hasAISuggestion,
  link,
}: SubSystemFieldProps): ReactElement => {
  const innerSx = useMemo(
    () => ({
      ...(hasAISuggestion && {
        backgroundColor: (theme: Theme) =>
          theme.palette.custom.OV.sys.aiSuggestionsTextBackground,
      }),
    }),
    [hasAISuggestion],
  );

  const autocompleteProps = useMemo<Record<string, unknown>>(
    () =>
      produce(externalAutocompleteProps ?? {}, (draft: Record<string, unknown>) => {
        const draftSx = (draft.sx ?? {}) as Record<string, unknown>;
        draft.sx = {
          ...draftSx,
          ...(hasAISuggestion ? autocompleteAISuggestionSx : {}),
        };

        // Ensure dropdown (Popper) can have a minimum width larger than the input if desired
        if (autocompleteMinWidth !== undefined) {
          const slotProps = (draft.slotProps ??= {}) as Record<string, unknown>;
          const popper = (slotProps.popper ??= {}) as Record<string, unknown>;
          const popperSx = (popper.sx ?? {}) as Record<string, unknown>;

          popper.sx = {
            ...popperSx,
            minWidth: autocompleteMinWidth,
          };
        }
      }),
    [externalAutocompleteProps, hasAISuggestion, autocompleteMinWidth],
  );

  const fieldProps = useMemo<Partial<FormField> & { wrapperSx?: SxProps }>(() => {
    switch (true) {
      case field.type === 'rich-text-bubble':
        return {
          type: 'rich-text-bubble' as const,
          wrapperSx: {
            flexDirection: 'column',
            alignItems: 'flex-start',
          },
          name: field.name,
          placeholder: field.placeholder,
          debounced: field.debounced || SAVE_DEBOUNCE_DELAY,
          sx: { width: '100%' },
          watchContent: true,
          onChange: (value: string) => onFieldChange(field.name, value),
        };
      case field.type === 'switch':
        return {
          type: 'switch' as const,
          name: field.name,
          onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
            onFieldChange(field.name, event.target.checked),
        };
      case !!field.options?.length: {
        const mapSelectionToValue = (value: unknown) => {
          if (typeof value === 'string') {
            return value;
          }

          if (Array.isArray(value)) {
            return null;
          }

          if (value && (value as { id?: unknown }).id !== undefined) {
            return String((value as { id: unknown }).id);
          }

          return null;
        };

        return {
          type: 'autocomplete' as const,
          name: field.name,
          inputProps,
          icon: field.icon ? field.icon.name : undefined,
          options: field.options ? [emptyOption, ...field.options] : [],

          textFieldProps: {
            ...textFieldProps,
            placeholder: 'Select Option',
          },
          autocompleteProps: {
            ...autocompleteProps,
            onChange: (_, value) => onFieldChange(field.name, mapSelectionToValue(value)),
          },
        };
      }
      default:
        return {
          type: 'textfield' as const,
          name: field.name,
          onChangeDebounceDelay: SAVE_DEBOUNCE_DELAY,
          pattern: field.pattern,
          textFieldProps: {
            ...textFieldProps,
            InputProps: inputProps,
            placeholder: 'Enter Value',
            onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
              onFieldChange(field.name, event.target.value),
          },
        };
    }
  }, [
    autocompleteProps,
    field.debounced,
    field.icon,
    field.name,
    field.options,
    field.pattern,
    field.placeholder,
    field.type,
    inputProps,
    onFieldChange,
    textFieldProps,
  ]);

  const { wrapperSx, ...restFieldProps } = fieldProps;

  return (
    <Stack direction="row" alignItems="center" gap={1} key={field.name}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        flexGrow={1}
        pl={2}
        pr={1}
        borderRadius={1}
        sx={{ ...innerSx, ...wrapperSx }}
      >
        <Typography
          variant="subtitle2"
          py={1}
          sx={{
            ...(!hasAISuggestion && {
              color: (theme) => theme.palette.custom.OV.sys.outline,
            }),
            flexShrink: 0,
            textWrap: 'wrap',
          }}
        >
          {field.label}
        </Typography>
        <Stack direction="row" width="100%" alignItems="center" justifyContent="end">
          {getFormField(restFieldProps as FormField, disabled)}

          {!!link && (
            <Tooltip
              title={
                <Stack direction="column" alignItems="center">
                  <span>{link}</span>
                  <span>Click to open</span>
                </Stack>
              }
            >
              <Link
                href={link}
                underline="none"
                color="inherit"
                target="_blank"
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <DynamicIcon name="arrow-up-right" library="lucide" size={19} />
              </Link>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {!!hasAISuggestion && (
        <IconButton
          onClick={() => onResetField(field.name)}
          size="small"
          color="primary"
          title="Reset to original"
        >
          <DynamicIcon name="rotate-cw" library="lucide" />
        </IconButton>
      )}
    </Stack>
  );
};
